import { Alert, AlertTitle, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Stack } from '@mui/material';
import { useSnackbar } from 'notistack';
import * as React from 'react';

import { describeError, formatTs } from './components';
import { BREAKER_REASONS } from './labels';
import type { BreakerAction, BreakerSummary, SandboxBreakerResetResult } from './types';

const pct = (ratio: number | undefined) => (typeof ratio === 'number' ? `${Math.round(ratio * 100)}%` : null);

const statsLine = (breaker: BreakerSummary) => {
    const s = breaker.stats;
    if (!s) {
        return null;
    }
    const window = s.window_minutes ? `over the last ${s.window_minutes} minutes` : 'recently';
    if (breaker.reason === 'rate_limited') {
        return `${s.rate_limited ?? 0} of ${s.answered ?? 0} requests were answered 429 ${window} (${pct(s.rate_limited_ratio) ?? '—'}).`;
    }
    return `${s.failed ?? 0} of ${s.admitted ?? 0} admitted requests failed ${window} (${pct(s.failure_ratio) ?? '—'}).`;
};

// Which trip a state belongs to: an "unreachable" result is about the trip it reset, not a later one.
const tripKey = (breaker: BreakerSummary | null) => `${breaker?.since ?? ''}|${breaker?.reason ?? ''}`;

const heldWords = (held: number | null | undefined) => {
    if (held == null) {
        return 'Held requests are stored and not delivered until a reset.';
    }
    return held === 1 ? '1 request is stored and not delivered until a reset.' : `${held} requests are stored and not delivered until a reset.`;
};

/** The circuit breaker (S6a; SBX-cb, round 4): a tripped breaker shows clearly, and the sandbox's
 *  developers (developer.outbound_manage) reset it through S6a's audited API, delivering or dropping
 *  what bproxy held meanwhile. */
const BreakerBanner = ({
    breaker,
    paused,
    canReset,
    onReset,
}: {
    breaker: BreakerSummary | null;
    paused: boolean | null;
    canReset: boolean;
    onReset: (action: BreakerAction) => Promise<SandboxBreakerResetResult>;
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const [confirm, setConfirm] = React.useState<BreakerAction | null>(null);
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState<unknown>(null);
    // The trip (tripKey) whose reset could not reach bproxy (held -1). Derived rather than cleared in an
    // effect: an effect keyed on since/reason would race the overview reload that follows the reset.
    // A new trip (another since or reason) no longer matches, so the stale warning disappears.
    const [unreachableAt, setUnreachableAt] = React.useState<string | null>(null);
    const latest = React.useRef(breaker);
    latest.current = breaker;
    const unreachable = unreachableAt !== null && unreachableAt === tripKey(breaker);

    if (!breaker) {
        return null;
    }
    const tripped = breaker.tripped || !!paused;
    const held = breaker.held;
    if (!tripped && !(held && held > 0) && !unreachable) {
        return null;
    }

    const reset = async (action: BreakerAction) => {
        setConfirm(null);
        setBusy(true);
        setError(null);
        try {
            const result = await onReset(action);
            if (result.held === -1) {
                // onReset reloads the overview first: key on the state after the reset.
                setUnreachableAt(tripKey(latest.current));
            } else {
                setUnreachableAt(null);
                const n = result.held === 1 ? '1 held request' : `${result.held} held requests`;
                enqueueSnackbar(`Circuit breaker reset: ${n} ${action === 'deliver' ? 'delivered' : 'dropped'}.`, { variant: 'success' });
            }
        } catch (e) {
            setError(e);
        } finally {
            setBusy(false);
        }
    };

    const actions = canReset ? (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5 }}>
            <Button variant="contained" color="primary" size="small" disabled={busy} onClick={() => setConfirm('deliver')}>
                Reset and deliver held requests
            </Button>
            <Button variant="outlined" color="inherit" size="small" disabled={busy} onClick={() => setConfirm('drop')}>
                Reset and drop held requests
            </Button>
        </Stack>
    ) : (
        <Box sx={{ mt: 1 }}>Only a developer with the outbound permission can reset it.</Box>
    );

    return (
        <Box data-testid="breaker-banner" sx={{ my: 1 }}>
            {tripped ? (
                <Alert severity="error" variant="filled">
                    <AlertTitle>Circuit breaker tripped: inbound requests are paused</AlertTitle>
                    {breaker.since && <>Since {formatTs(breaker.since)}{breaker.reason ? `, because of ${BREAKER_REASONS[breaker.reason] || breaker.reason}` : ''}. </>}
                    {statsLine(breaker)} {heldWords(held)}
                    {actions}
                </Alert>
            ) : (
                <Alert severity="warning">
                    <AlertTitle>Held requests</AlertTitle>
                    {unreachable
                        ? 'The breaker was reset, but the held requests could not be reached. Reset again to release them.'
                        : heldWords(held)}
                    {actions}
                </Alert>
            )}
            {tripped && unreachable && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                    The held requests could not be reached. Reset again to release them.
                </Alert>
            )}
            {error ? <Alert severity="error" sx={{ mt: 1 }}>{describeError(error)}</Alert> : null}
            <Dialog open={!!confirm} onClose={() => setConfirm(null)} aria-labelledby="breaker-reset-title">
                <DialogTitle id="breaker-reset-title">
                    {confirm === 'drop' ? 'Reset and drop held requests?' : 'Reset and deliver held requests?'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {confirm === 'drop'
                            ? 'The breaker resets and every held request is dropped: it is never delivered to the sandbox. Senders get no further answer.'
                            : 'The breaker resets and every held request is delivered to the sandbox, oldest first. New requests are admitted again.'}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirm(null)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color={confirm === 'drop' ? 'error' : 'primary'}
                        onClick={() => confirm && reset(confirm)}
                    >
                        {confirm === 'drop' ? 'Drop and reset' : 'Deliver and reset'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default BreakerBanner;
